import React, { useState, useRef } from 'react';
import ToolLayout from '../components/ToolLayout';

type ConversionMode = 'toImage' | 'toBase64';

const Base64ImageConverter: React.FC = () => {
  const [mode, setMode] = useState<ConversionMode>('toBase64');
  const [base64Input, setBase64Input] = useState<string>('');
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageInfo, setImageInfo] = useState<{
    size: string;
    type: string;
    dimensions?: string;
  } | null>(null);
  const [error, setError] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Convert image file to base64
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setImagePreview(result);
      setBase64Input(result);
      setError('');

      // Get image dimensions
      const img = new Image();
      img.onload = () => {
        setImageInfo({
          size: formatBytes(file.size),
          type: file.type,
          dimensions: `${img.width}x${img.height}`
        });
      };
      img.src = result;
    };
    reader.onerror = () => {
      setError('Error reading file');
    };
    reader.readAsDataURL(file);
  };

  // Convert base64 to image preview
  const handleBase64ToImage = () => {
    setError('');
    setCopySuccess(false);

    if (!base64Input.trim()) {
      setError('Please enter a Base64 string');
      return;
    }

    // Check if it's a valid base64 string
    const base64Pattern = /^data:image\/(png|jpeg|jpg|gif|webp|svg\+xml);base64,/i;
    let base64String = base64Input.trim();

    // If it doesn't have the data URL prefix, try to add it
    if (!base64Pattern.test(base64String)) {
      // Try to detect if it's raw base64
      if (/^[A-Za-z0-9+/]+=*$/.test(base64String.replace(/\s/g, ''))) {
        // Assume PNG if no type specified
        base64String = `data:image/png;base64,${base64String}`;
      } else {
        setError('Invalid Base64 string. Please provide a valid Base64 encoded image.');
        return;
      }
    }

    // Try to load the image
    const img = new Image();
    img.onload = () => {
      setImagePreview(base64String);

      // Calculate approximate size
      const base64Length = base64String.split(',')[1]?.length || 0;
      const sizeInBytes = (base64Length * 3) / 4;

      setImageInfo({
        size: formatBytes(sizeInBytes),
        type: base64String.match(/data:image\/([^;]+)/)?.[1] || 'unknown',
        dimensions: `${img.width}x${img.height}`
      });
      setError('');
    };
    img.onerror = () => {
      setError('Invalid image data. The Base64 string does not represent a valid image.');
      setImagePreview('');
      setImageInfo(null);
    };
    img.src = base64String;
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleCopy = () => {
    if (base64Input) {
      navigator.clipboard.writeText(base64Input).then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      });
    }
  };

  const handleDownload = () => {
    if (!imagePreview) {
      return;
    }

    const link = document.createElement('a');
    link.href = imagePreview;
    const ext = imageInfo?.type || 'png';
    link.download = `image-${Date.now()}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClear = () => {
    setBase64Input('');
    setImagePreview('');
    setImageInfo(null);
    setError('');
    setCopySuccess(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const event = {
        target: { files: [file] }
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      handleImageUpload(event);
    } else {
      setError('Please drop a valid image file');
    }
  };

  return (
    <ToolLayout
      title="Base64 Image Converter"
      description="Convert images to Base64 and decode Base64 strings to images"
    >
      <div className="base64-image-converter">
        {/* Mode Selection */}
        <div className="mode-selector">
          <button
            className={`mode-button ${mode === 'toBase64' ? 'active' : ''}`}
            onClick={() => {
              setMode('toBase64');
              handleClear();
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <path d="M21 15l-5-5L5 21"/>
            </svg>
            Image to Base64
          </button>
          <button
            className={`mode-button ${mode === 'toImage' ? 'active' : ''}`}
            onClick={() => {
              setMode('toImage');
              handleClear();
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            Base64 to Image
          </button>
        </div>

        {/* Image to Base64 Mode */}
        {mode === 'toBase64' && (
          <>
            <div className="input-group">
              <label className="input-label">Upload Image</label>
              <div
                className="file-drop-zone"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <p className="drop-text">Drop an image here or click to browse</p>
                <p className="drop-hint">Supports: PNG, JPG, GIF, WebP, SVG</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </div>
          </>
        )}

        {/* Base64 to Image Mode */}
        {mode === 'toImage' && (
          <>
            <div className="input-group">
              <label className="input-label">Base64 String</label>
              <textarea
                className="input-textarea"
                value={base64Input}
                onChange={(e) => setBase64Input(e.target.value)}
                placeholder="Paste your Base64 string here... (with or without data:image prefix)"
                rows={8}
              />
            </div>

            <div className="button-group">
              <button className="action-button" onClick={handleBase64ToImage}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
                  <path d="M21 3v5h-5"/>
                </svg>
                Convert to Image
              </button>
              <button className="action-button secondary" onClick={handleClear}>
                Clear
              </button>
            </div>
          </>
        )}

        {/* Error Message */}
        {error && <div className="error-message">{error}</div>}

        {/* Copy Success Message */}
        {copySuccess && <div className="success-message">Base64 copied to clipboard!</div>}

        {/* Image Preview and Results */}
        {imagePreview && (
          <div className="result-section">
            <div className="result-header">
              <h3 className="result-title">Result</h3>
              <div className="button-group">
                {mode === 'toBase64' && (
                  <button className="action-button secondary" onClick={handleCopy}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                    Copy Base64
                  </button>
                )}
                <button className="action-button secondary" onClick={handleDownload}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Download
                </button>
              </div>
            </div>

            {/* Image Info */}
            {imageInfo && (
              <div className="image-info">
                <div className="info-item">
                  <span className="info-label">Type:</span>
                  <span className="info-value">{imageInfo.type.toUpperCase()}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Size:</span>
                  <span className="info-value">{imageInfo.size}</span>
                </div>
                {imageInfo.dimensions && (
                  <div className="info-item">
                    <span className="info-label">Dimensions:</span>
                    <span className="info-value">{imageInfo.dimensions}</span>
                  </div>
                )}
              </div>
            )}

            {/* Image Preview */}
            <div className="image-preview-container">
              <img src={imagePreview} alt="Preview" className="image-preview" />
            </div>

            {/* Base64 Output */}
            {mode === 'toBase64' && (
              <div className="base64-output">
                <div className="output-header">
                  <label className="input-label">Base64 Output</label>
                  <span className="output-length">
                    {base64Input.length.toLocaleString()} characters
                  </span>
                </div>
                <textarea
                  className="output-textarea"
                  value={base64Input}
                  readOnly
                  rows={6}
                />
              </div>
            )}
          </div>
        )}

        {/* Info Section */}
        <div className="info-section">
          <h3 className="info-title">About Base64 Image Conversion</h3>
          <ul className="info-list">
            <li><strong>Image to Base64:</strong> Converts image files to Base64 encoded strings. Useful for embedding images in HTML, CSS, or JSON.</li>
            <li><strong>Base64 to Image:</strong> Decodes Base64 strings back to viewable images. Supports both data URLs and raw Base64.</li>
            <li><strong>Supported Formats:</strong> PNG, JPEG, GIF, WebP, SVG and most common image formats.</li>
            <li><strong>Use Cases:</strong> Email templates, data URIs, API responses, inline images in web pages.</li>
            <li><strong>Note:</strong> Base64 encoding increases file size by approximately 33%. Use for small images or when embedding is necessary.</li>
          </ul>
        </div>
      </div>

      <style>{`
        .base64-image-converter {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .mode-selector {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .mode-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 1rem;
          background: var(--bg-secondary);
          border: 2px solid var(--border-color);
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .mode-button:hover {
          border-color: var(--accent-primary);
          transform: translateY(-2px);
        }

        .mode-button.active {
          background: var(--accent-light);
          border-color: var(--accent-primary);
          color: var(--accent-primary);
        }

        .file-drop-zone {
          border: 2px dashed var(--border-color);
          border-radius: 12px;
          padding: 3rem 2rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          background: var(--bg-secondary);
        }

        .file-drop-zone:hover {
          border-color: var(--accent-primary);
          background: var(--accent-light);
        }

        .file-drop-zone svg {
          color: var(--text-tertiary);
          margin-bottom: 1rem;
        }

        .drop-text {
          font-size: 1rem;
          color: var(--text-primary);
          margin-bottom: 0.5rem;
          font-weight: 500;
        }

        .drop-hint {
          font-size: 0.875rem;
          color: var(--text-tertiary);
        }

        .result-section {
          margin-top: 1.5rem;
          padding: 1.5rem;
          background: var(--bg-secondary);
          border-radius: 12px;
          border: 1px solid var(--border-color);
        }

        .result-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .result-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--text-primary);
        }

        .image-info {
          display: flex;
          gap: 2rem;
          margin-bottom: 1.5rem;
          padding: 1rem;
          background: var(--bg-primary);
          border-radius: 10px;
          flex-wrap: wrap;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .info-label {
          font-size: 0.8125rem;
          color: var(--text-tertiary);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .info-value {
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .image-preview-container {
          margin-bottom: 1.5rem;
          padding: 1rem;
          background: var(--bg-primary);
          border-radius: 10px;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 200px;
        }

        .image-preview {
          max-width: 100%;
          max-height: 500px;
          border-radius: 8px;
          box-shadow: var(--shadow-md);
        }

        .base64-output {
          margin-top: 1rem;
        }

        .output-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .output-length {
          font-size: 0.8125rem;
          color: var(--text-tertiary);
        }

        .output-textarea {
          width: 100%;
          padding: 1rem;
          background: var(--bg-primary);
          border: 2px solid var(--border-color);
          border-radius: 10px;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.8125rem;
          color: var(--text-secondary);
          resize: vertical;
          line-height: 1.5;
        }

        .output-textarea:focus {
          outline: none;
          border-color: var(--accent-primary);
        }

        @media (max-width: 768px) {
          .mode-selector {
            grid-template-columns: 1fr;
          }

          .result-header {
            flex-direction: column;
            align-items: stretch;
          }

          .image-info {
            flex-direction: column;
            gap: 1rem;
          }

          .mode-button {
            font-size: 0.9375rem;
            padding: 0.875rem;
          }
        }
      `}</style>
    </ToolLayout>
  );
};

export default Base64ImageConverter;